import { createReducer, on } from '@ngrx/store'; // Todo
import { getEmbeddedResource, getUrl } from '@ngxp/rest';
import { createEmptyStateResource, createStateResource, StateResource } from '@schir-int-client/ngrx-helpers';
import { loadVerfahrenSingleSuccessAction } from '@schir-int-client/verfahren-shared';
import {
	createKorrespondenzVerfuegungByVorgangSuccessAction,
	createVerfuegungByVorgangSuccessAction,
	deleteVerfuegungSuccessAction,
	loadVerfuegungSuccessAction,
	setSelectedVerfuegungAction,
} from '@schir-int-client/verfuegung-shared';
import {
	createKorrespondenzVorlageSuccessAction,
	deleteKorrespondenzVorlageSuccessAction,
	loadKorrespondenzVorlageAction,
	loadKorrespondenzVorlageSuccessAction,
} from 'libs/korrespondenz-shared/src/lib/korrespondenz-vorlage/korrespondenz-vorlage.actions';
import { KorrespondenzVorlageResource } from 'libs/korrespondenz-shared/src/lib/korrespondenz-vorlage/korrespondenz-vorlage.model';
// barrel-import würde Zyklus schließen:
import { loadVorlageSuccessAction } from 'libs/vorlage-shared/src/lib/vorlage.actions';
import type { VorlageResource } from 'libs/vorlage-shared/src/lib/vorlage.model';
import { isNil } from 'lodash-es';
import {
	clearAdressatenByAufgabeAction,
	clearSelectedAufgabeAction,
	createAufgabeSuccessAction,
	enrichAdressatenAction,
	loadAufgabenByVerfuegungAction,
	loadAufgabenByVerfuegungSuccessAction,
	loadAufgabeSuccessAction,
	updateAufgabeSuccessAction,
} from './aufgabe.actions';
import { AufgabeLinkRel, AufgabeListLinkRel } from './aufgabe.linkrel';
import { AdressatWithResource, AufgabeListResource, AufgabeResource } from './aufgabe.model';
import { AufgabeService } from './aufgabe.service';
import { updateKontaktSuccessAction } from '@schir-int-client/adressverwaltung-shared';
import { RegisterName } from '@schir-int-client/register-shared';
import { AdressatLinkRel } from './adressat/adressat.linkrel';

export interface AufgabeState {
	selectedAufgabe: AufgabeResource;
	aufgaben: StateResource<AufgabeListResource>;
	adressatenByAufgabe: {
		[aufgabeUri: string]: AdressatWithResource[];
	};
	vorlagenResourcesByUri: {
		[vorlageUri: string]: VorlageResource;
	};
	korrespondenzVorlageByUri: {
		[korrespondenzVorlageUri: string]: StateResource<KorrespondenzVorlageResource>;
	};
}

export const initialState: AufgabeState = {
	selectedAufgabe: null,
	aufgaben: createEmptyStateResource(),
	adressatenByAufgabe: {},
	vorlagenResourcesByUri: {},
	korrespondenzVorlageByUri: {},
};

export const aufgabeReducer = createReducer(
	initialState,
	on(createAufgabeSuccessAction, (state: AufgabeState, { aufgabe }): AufgabeState => ({
		...state,
		selectedAufgabe: aufgabe,
		aufgaben: { ...state.aufgaben, reload: true },
	})),
	on(updateAufgabeSuccessAction, (state: AufgabeState): AufgabeState => ({
		...state,
		aufgaben: { ...state.aufgaben, reload: true },
	})),
	on(loadAufgabenByVerfuegungAction, (state: AufgabeState): AufgabeState => ({
		...state,
		aufgaben: { ...state.aufgaben, loading: true },
	})),
	on(
		loadAufgabenByVerfuegungSuccessAction,
		(state: AufgabeState, { aufgaben, verfuegung, register }): AufgabeState => {
			if (
				state.selectedAufgabe &&
				verfuegung._links.self.href === state.selectedAufgabe._links['verfuegung'].href
			) {
				return {
					...state,
					aufgaben: createStateResource(aufgaben),
				};
			}
			return {
				...state,
				aufgaben: createStateResource(aufgaben),
				selectedAufgabe: getFirstAufgabeAfterSorting(aufgaben, register),
			};
		}
	),
	on(loadAufgabeSuccessAction, (state: AufgabeState, { aufgabe }): AufgabeState => ({
		...state,
		selectedAufgabe: aufgabe,
	})),
	on(setSelectedVerfuegungAction, loadVerfuegungSuccessAction, (state: AufgabeState): AufgabeState => ({
		...state,
		aufgaben: { ...state.aufgaben, reload: true },
	})),
	on(enrichAdressatenAction, (state: AufgabeState, { adressaten, aufgabeUri }): AufgabeState => {
		let adressatenByAufgabe = undefined;
		if (!isNil(adressaten)) {
			adressatenByAufgabe = adressaten;
		}
		return {
			...state,
			adressatenByAufgabe: {
				...state.adressatenByAufgabe,
				[aufgabeUri]: adressatenByAufgabe,
			},
		};
	}),
	on(clearAdressatenByAufgabeAction, (state: AufgabeState, { aufgabeUri }): AufgabeState => ({
		...state,
		adressatenByAufgabe: {
			...state.adressatenByAufgabe,
			[aufgabeUri]: undefined,
		},
	})),
	on(loadVorlageSuccessAction, (state: AufgabeState, { vorlage }): AufgabeState => ({
		...state,
		vorlagenResourcesByUri: {
			...state.vorlagenResourcesByUri,
			[getUrl(vorlage)]: vorlage,
		},
	})),
	on(loadVerfahrenSingleSuccessAction, (state: AufgabeState, { verfahren, predecessor }): AufgabeState => {
		if (!predecessor || (verfahren && predecessor && predecessor.aktenzeichen === verfahren.aktenzeichen)) {
			return state;
		}
		return {
			...state,
			selectedAufgabe: null,
		};
	}),
	on(
		createKorrespondenzVorlageSuccessAction,
		deleteKorrespondenzVorlageSuccessAction,
		(state: AufgabeState): AufgabeState => ({
			...state,
			aufgaben: { ...state.aufgaben, reload: true },
		})
	),
	on(clearSelectedAufgabeAction, (state: AufgabeState): AufgabeState => ({
		...state,
		selectedAufgabe: null,
	})),
	on(loadKorrespondenzVorlageAction, (state: AufgabeState, { aufgabe }): AufgabeState => ({
		...state,
		korrespondenzVorlageByUri: {
			...state.korrespondenzVorlageByUri,
			[getUrl(aufgabe, AufgabeLinkRel.KORRESPONDENZ_VORLAGE)]: createEmptyStateResource(true),
		},
	})),
	on(loadKorrespondenzVorlageSuccessAction, (state: AufgabeState, { korrespondenzVorlage }): AufgabeState => ({
		...state,
		korrespondenzVorlageByUri: {
			...state.korrespondenzVorlageByUri,
			[getUrl(korrespondenzVorlage)]: createStateResource(korrespondenzVorlage),
		},
	})),
	on(createVerfuegungByVorgangSuccessAction, (state: AufgabeState): AufgabeState => ({
		...state,
		aufgaben: { ...state.aufgaben, reload: true },
	})),
	on(createKorrespondenzVerfuegungByVorgangSuccessAction, (state: AufgabeState): AufgabeState => ({
		...state,
		aufgaben: { ...state.aufgaben, reload: true },
	})),
	on(updateKontaktSuccessAction, (state: AufgabeState, { kontakt }): AufgabeState => {
		const updatedAdressatenByAufgabe = { ...state.adressatenByAufgabe };

		Object.keys(updatedAdressatenByAufgabe).forEach(aufgabeUri => {
			const adressaten: AdressatWithResource[] = [...(updatedAdressatenByAufgabe[aufgabeUri] || [])];
			if (adressaten) {
				adressaten.forEach((adressat, index) => {
					if (getUrl(adressat, AdressatLinkRel.KONTAKT) === kontakt._links.self.href) {
						adressaten[index] = { ...adressaten[index], resource: kontakt };
					}
				});
			}
			updatedAdressatenByAufgabe[aufgabeUri] = adressaten;
		});
		return {
			...state,
			adressatenByAufgabe: updatedAdressatenByAufgabe,
		};
	}),
	on(deleteVerfuegungSuccessAction, (state: AufgabeState, { deleted }): AufgabeState => {
		if (state.selectedAufgabe?._links['verfuegung'].href == deleted._links.self.href) {
			return {
				...initialState,
				aufgaben: { ...createEmptyStateResource() },
				selectedAufgabe: null,
			};
		}
		return state;
	})
);

//required because component sorts aufgaben before rendering
function getFirstAufgabeAfterSorting(resource: AufgabeListResource, register: RegisterName) {
	const aufgaben = getEmbeddedResource<AufgabeResource[]>(resource, AufgabeListLinkRel.AUFGABE_LIST);
	const sorted: AufgabeResource[] = AufgabeService.sortAufgabenByTyp(aufgaben, register);
	return sorted?.[0] ?? null;
}
